import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Camera, Upload, Trash2, Loader2, Search, Copy, 
  Monitor, RefreshCw, Eye, X, Edit, Download, ExternalLink,
  Code, Bug, Layout, TestTube, Sparkles, Filter
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScreenshotsManager({ adminKey }) {
  const { t } = useLanguage();
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'development',
    page_url: '',
    tags: ''
  });

  const fetchScreenshots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/screenshots`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await response.json();
      setScreenshots(data.screenshots || []);
    } catch (error) {
      toast.error(t('admin.load_error'));
    } finally {
      setLoading(false);
    }
  }, [adminKey, t]);

  useEffect(() => {
    fetchScreenshots();
  }, [fetchScreenshots]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData(prev => ({ ...prev, title: file.name.split('.')[0] }));
      setUploadDialog(true);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error(t('admin.select_file'));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', uploadData.title || file.name);
    formData.append('description', uploadData.description);
    formData.append('category', uploadData.category);
    formData.append('page_url', uploadData.page_url);
    formData.append('tags', uploadData.tags);

    try {
      const response = await fetch(`${API_URL}/api/admin/screenshots/upload`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      toast.success(t('admin.screenshot_uploaded'));
      setUploadDialog(false);
      setUploadData({ title: '', description: '', category: 'development', page_url: '', tags: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchScreenshots();
    } catch (error) {
      toast.error(t('admin.upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (screenshotId) => {
    if (!window.confirm(t('admin.delete') + '?')) return;

    try {
      await fetch(`${API_URL}/api/admin/screenshots/${screenshotId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success(t('admin.screenshot_deleted'));
      setScreenshots(prev => prev.filter(s => s.screenshot_id !== screenshotId));
      if (selectedScreenshot?.screenshot_id === screenshotId) {
        setSelectedScreenshot(null);
        setPreviewDialog(false);
      }
    } catch (error) {
      toast.error(t('admin.delete_error'));
    }
  };

  const handleUpdate = async () => {
    if (!selectedScreenshot) return;

    try {
      await fetch(`${API_URL}/api/admin/screenshots/${selectedScreenshot.screenshot_id}`, {
        method: 'PUT',
        headers: { 
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: selectedScreenshot.title,
          description: selectedScreenshot.description,
          category: selectedScreenshot.category,
          page_url: selectedScreenshot.page_url,
          tags: selectedScreenshot.tags
        })
      });
      toast.success(t('admin.screenshot_updated'));
      setEditDialog(false);
      fetchScreenshots();
    } catch (error) {
      toast.error(t('admin.config_save_error'));
    }
  };

  const copyUrl = (url) => {
    const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t('admin.url_copied'));
  };

  const downloadScreenshot = (screenshot) => {
    const link = document.createElement('a');
    link.href = `${API_URL}${screenshot.url}`;
    link.download = screenshot.filename || 'screenshot.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Categories with translations
  const categories = [
    { value: 'all', label: t('admin.all_filter'), icon: Filter },
    { value: 'development', label: t('admin.development_filter'), icon: Code },
    { value: 'feature', label: t('admin.feature_filter'), icon: Sparkles },
    { value: 'bug', label: t('admin.bug_filter'), icon: Bug },
    { value: 'ui', label: t('admin.ui_filter'), icon: Layout },
    { value: 'test', label: t('admin.test_filter'), icon: TestTube },
    { value: 'auto', label: t('admin.auto_capture_filter'), icon: Camera }
  ];

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.icon || Camera;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  };

  // Filter screenshots
  const filteredScreenshots = screenshots.filter(s => {
    const matchesSearch = 
      s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || s.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Camera className="h-6 w-6" />
            {t('admin.screenshots_gallery')}
          </h2>
          <p className="text-muted-foreground">{screenshots.length} {t('admin.captures_count')} • {t('admin.visual_documentation')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchScreenshots} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('admin.refresh')}
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {t('admin.upload')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder={t('admin.search_screenshots')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <Button
                key={cat.value}
                variant={filterCategory === cat.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterCategory(cat.value)}
                className="gap-1"
              >
                <Icon className="h-3 w-3" />
                {cat.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Screenshots Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredScreenshots.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <Camera className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterCategory !== 'all' ? t('admin.no_screenshot_found') : t('admin.no_screenshot_uploaded')}
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              {t('admin.upload_first_screenshot')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredScreenshots.map(screenshot => {
            const CategoryIcon = getCategoryIcon(screenshot.category);
            return (
              <Card 
                key={screenshot.screenshot_id}
                className="group cursor-pointer overflow-hidden hover:shadow-lg transition-all"
                onClick={() => { setSelectedScreenshot(screenshot); setPreviewDialog(true); }}
              >
                <div className="aspect-video relative bg-muted">
                  {screenshot.url ? (
                    <img 
                      src={`${API_URL}${screenshot.url}`}
                      alt={screenshot.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera className="h-8 w-8 text-muted-foreground opacity-50" />
                    </div>
                  )}
                  {/* Category Badge */}
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/60 text-white text-xs flex items-center gap-1">
                    <CategoryIcon className="h-3 w-3" />
                    {getCategoryLabel(screenshot.category)}
                  </div>
                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="secondary" className="h-8">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="h-8"
                      onClick={(e) => { e.stopPropagation(); downloadScreenshot(screenshot); }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="h-8"
                      onClick={(e) => { e.stopPropagation(); handleDelete(screenshot.screenshot_id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-sm truncate">{screenshot.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{screenshot.description || '-'}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted-foreground">{formatDate(screenshot.created_at)}</span>
                    <span className="text-xs text-muted-foreground">{formatFileSize(screenshot.size)}</span>
                  </div>
                  {screenshot.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {screenshot.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-muted rounded">{tag}</span>
                      ))}
                      {screenshot.tags.length > 3 && (
                        <span className="text-xs text-muted-foreground">+{screenshot.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.upload_screenshot')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('admin.title')} *</Label>
              <Input 
                value={uploadData.title}
                onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('admin.title')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.description')}</Label>
              <Textarea 
                value={uploadData.description}
                onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('admin.optional_description')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.category')}</Label>
                <Select value={uploadData.category} onValueChange={(v) => setUploadData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">🔧 {t('admin.development_filter')}</SelectItem>
                    <SelectItem value="feature">✨ {t('admin.feature_filter')}</SelectItem>
                    <SelectItem value="bug">🐛 {t('admin.bug_filter')}</SelectItem>
                    <SelectItem value="ui">🎨 {t('admin.ui_filter')}</SelectItem>
                    <SelectItem value="test">🧪 {t('admin.test_filter')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin.page_url')}</Label>
                <Input 
                  value={uploadData.page_url}
                  onChange={(e) => setUploadData(prev => ({ ...prev, page_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('admin.tags_comma')}</Label>
              <Input 
                value={uploadData.tags}
                onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder={t('admin.tags_placeholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>{t('admin.cancel')}</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {t('admin.upload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedScreenshot?.title}</DialogTitle>
          </DialogHeader>
          {selectedScreenshot && (
            <div className="space-y-4">
              {/* Image */}
              <div className="bg-muted rounded-lg overflow-hidden">
                {selectedScreenshot.url ? (
                  <img 
                    src={`${API_URL}${selectedScreenshot.url}`}
                    alt={selectedScreenshot.title}
                    className="w-full h-auto max-h-[60vh] object-contain"
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <Camera className="h-16 w-16 text-muted-foreground opacity-50" />
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.description')}</Label>
                  <p className="text-sm">{selectedScreenshot.description || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.category')}</Label>
                  <p className="text-sm">{getCategoryLabel(selectedScreenshot.category)}</p>
                </div>
                {selectedScreenshot.page_url && (
                  <div className="space-y-2">
                    <Label>{t('admin.page_url')}</Label>
                    <a href={selectedScreenshot.page_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                      {selectedScreenshot.page_url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{t('admin.date')}</Label>
                  <p className="text-sm">{formatDate(selectedScreenshot.created_at)}</p>
                </div>
              </div>

              {selectedScreenshot.tags?.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('admin.tags')}</Label>
                  <div className="flex flex-wrap gap-1">
                    {selectedScreenshot.tags.map((tag, i) => (
                      <span key={i} className="text-sm px-2 py-1 bg-muted rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => copyUrl(selectedScreenshot.url)} className="gap-2">
                  <Copy className="h-4 w-4" />
                  {t('admin.copy_url')}
                </Button>
                <Button variant="outline" onClick={() => downloadScreenshot(selectedScreenshot)} className="gap-2">
                  <Download className="h-4 w-4" />
                  {t('admin.download')}
                </Button>
                <Button variant="outline" onClick={() => { setPreviewDialog(false); setEditDialog(true); }} className="gap-2">
                  <Edit className="h-4 w-4" />
                  {t('admin.edit')}
                </Button>
                <Button variant="destructive" onClick={() => handleDelete(selectedScreenshot.screenshot_id)} className="gap-2">
                  <Trash2 className="h-4 w-4" />
                  {t('admin.delete')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.edit_screenshot')}</DialogTitle>
          </DialogHeader>
          {selectedScreenshot && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.title')}</Label>
                <Input 
                  value={selectedScreenshot.title}
                  onChange={(e) => setSelectedScreenshot(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.description')}</Label>
                <Textarea 
                  value={selectedScreenshot.description || ''}
                  onChange={(e) => setSelectedScreenshot(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.category')}</Label>
                <Select 
                  value={selectedScreenshot.category} 
                  onValueChange={(v) => setSelectedScreenshot(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">🔧 {t('admin.development_filter')}</SelectItem>
                    <SelectItem value="feature">✨ {t('admin.feature_filter')}</SelectItem>
                    <SelectItem value="bug">🐛 {t('admin.bug_filter')}</SelectItem>
                    <SelectItem value="ui">🎨 {t('admin.ui_filter')}</SelectItem>
                    <SelectItem value="test">🧪 {t('admin.test_filter')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>{t('admin.cancel')}</Button>
            <Button onClick={handleUpdate}>{t('admin.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
